/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {FormattedMessage, useIntl} from 'react-intl'
import {Helmet} from 'react-helmet'
import {
    useCategory,
    useCustomerId,
    useShopperCustomersMutation,
    useProductSearch
} from '@salesforce/commerce-sdk-react'
import Seo from '../../components/seo'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useHeaderContext} from '../../contexts/header-context'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useScript, setInitialScriptState} from '../../contexts/script-context'
import {generateAlternateUrls} from '../../utils/utils'

// Components
import {
    Box,
    Flex,
    SimpleGrid,
    Grid,
    Heading,
    Text,
    Stack,
    HStack,
    useDisclosure,
    Button,
    Center,
    Divider,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalContent,
    ModalCloseButton,
    ModalOverlay,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Select,
    Spinner,
    useBreakpointValue,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import Refinements from './partials/refinements'
import CategoryLinks from './partials/category-links'
import SelectedRefinements from './partials/selected-refinements'
import EmptySearchResults from './partials/empty-results'
import EmptySearchResultsTitle from './partials/empty-results-title'
import PageHeader from './partials/page-header'
import AbovePageHeader from './partials/above-page-header'
import PageFilters from './partials/page-filters'
import GridInteruptorBlock from '../../components/optimizely/common/gridInteruptorBlock'
import OptimizelyArea from '../../components/optimizely/common/optimizelyArea'

// Icons
import {ChevronDownIcon, CheckIcon} from '../../components/icons'

// Hooks
import {useLimitUrls, useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks/use-currency'
import {useSortUrls} from '../../hooks/use-sort-urls'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useViewportTracking} from '../../hooks/use-viewport-tracking'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Others
import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {renderLayoutsSwitch} from '../../components/optimizely/utils/contentRenderer'

import {useCustomToast} from '../../contexts/custom-toast-context'
import {mergeRefs} from '../../utils/ref-utils'
import {useProductSearchSSR} from '../../hooks/use-product-search-ssr'

// Constants
import {
    DEFAULT_LIMIT_VALUES,
    API_ERROR_MESSAGE,
    MAX_CACHE_AGE,
    STALE_WHILE_REVALIDATE,
    PRODUCT_LIST_IMAGE_VIEW_TYPE,
    PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {getWishlistId} from '../../utils/wishlist-utils'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import {useClientCategoryId} from '../../hooks/use-client-category-id'
import {categoryUrlBuilder} from '../../utils/url'
import {
    sendViewItemListEvent,
    sendViewSearchResultsEvent,
    sendSelectItemEvent,
    sendWishlistEvent,
    setTempRecommendation
} from '../../utils/gtm-utils'
import {sendBrazeWishlistEvent, sendBrazeViewItemListEvent, sendBrazeViewSearchResultsEvent} from '../../utils/braze-utils'

// Error page
import PageNotFound from '../page-not-found'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']

const {app: globalConfig} = getConfig()

if (globalConfig.IS_NOIBU_DISABLED_PLP === 'true') {
    setInitialScriptState('noibu', false)
}

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */

const ProductList = (props) => {
    // Using destructuring to omit properties; we must rename `isLoading` because we use a different
    // `isLoading` later in this function.
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    const {enableScript, disableScript} = useScript()
    const {isLoading: _unusedIsLoading, staticContext, ...rest} = props
    const {isOpen, onClose, onOpen} = useDisclosure()
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const history = useHistory()
    const params = useParams()
    const location = useLocation()
    const {buildUrl, locale} = useMultiSite()
    const {currency} = useCurrency()
    const localeId = locale?.id || locale?.alias
    const einstein = useEinstein()
    const activeData = useActiveData()
    const {res, req} = useServerContext()
    const customerId = useCustomerId()
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const {showCustomToast} = useCustomToast()
    const config = getConfig()

    // Get SEO data from server context (set by SSR endpoint) for SSR
    const serverContext = useServerContext()
    const serverSeoData = serverContext?.req?.seoData || null

    // Get current category path from URL
    const urlCategoryPath = params.categoryPath || params.categoryId || ''

    // State to persist SEO data once captured
    const [seoData, setSeoData] = useState(() => {
        // Initialize with server data for SSR
        if (typeof window === 'undefined') {
            return serverSeoData
        }

        const windowSeoData = window.__SEO_DATA__

        // Only use window.__SEO_DATA__ if it matches the current URL's category path
        // This prevents stale SEO data from being used when navigating back
        if (windowSeoData && windowSeoData.categoryPath !== urlCategoryPath) {
            return null
        }

        // Initialize with window data for client hydration (first load only)
        return windowSeoData || null
    })

    // Clear seoData when the category path in the URL changes
    // This handles navigation between different categories
    const currentCategoryPath = params.categoryPath || params.categoryId || ''
    const lastCategoryPathRef = useRef(currentCategoryPath)

    useEffect(() => {
        // If category path changed and we have stale seoData, clear it
        if (currentCategoryPath !== lastCategoryPathRef.current && seoData) {
            setSeoData(null)
        }

        lastCategoryPathRef.current = currentCategoryPath
    }, [currentCategoryPath, seoData, location.pathname])

    // For client-side SEO URLs, resolve categoryId when we have SEO URL params
    // Always try to resolve client-side if we have SEO URL structure
    const hasSEOUrlParams = params.brandId && params.categoryPath
    const {data: clientCategoryId, error: clientCategoryError} = useClientCategoryId(
        params.brandId,
        params.categoryPath,
        false // Always allow client-side resolution for SEO URLs
    )

    // For SEO URLs, prefer server data for SSR, then client-resolved for navigation
    // For standard URLs, use the URL param directly
    // Also support cgid query parameter for fallback category links (e.g., /search?cgid=xxx)
    // Memoize to prevent unnecessary recalculations and downstream re-renders
    const cgidFromQuery = searchParams.cgid
    const effectiveCategoryId = useMemo(() => {
        if (hasSEOUrlParams) {
            return seoData?.resolvedCategoryId || clientCategoryId
        }
        return params.categoryId || cgidFromQuery
    }, [hasSEOUrlParams, seoData, clientCategoryId, params.categoryId, cgidFromQuery])

    const serverSeoNotFound = Boolean(serverSeoData?.seoNotFound || seoData?.seoNotFound)
    const clientSeoNotFound =
        hasSEOUrlParams &&
        !serverSeoNotFound &&
        !!clientCategoryError &&
        (clientCategoryError?.message?.includes('404') || false)

    /**************** Page State ****************/
    const [filtersLoading, setFiltersLoading] = useState(false)
    const [wishlistLoading, setWishlistLoading] = useState([])
    const [sortOpen, setSortOpen] = useState(false)

    const urlParams = new URLSearchParams(location.search)
    const isSmartSearchParam = urlParams.get('smartSearch') === 'true'
    const initialPage = parseInt(urlParams.get('page')) || 1
    const [page, setPage] = useState(initialPage)
    const [minPageLoaded, setMinPageLoaded] = useState(initialPage)
    const [maxPageLoaded, setMaxPageLoaded] = useState(initialPage)
    const productRefs = useRef({})
    const productPageMap = useRef({})

    const [skeletonPlaceholders, setSkeletonPlaceholders] = useState([])
    const [isLoadingNewPage, setIsLoadingNewPage] = useState(false)
    const [isInitialLoad, setIsInitialLoad] = useState(true)
    const [currentPath, setCurrentPath] = useState(location.pathname)
    const appOrigin = useAppOrigin()
    const {setCategory} = useHeaderContext()

    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery

    /**************** CMS ****************/
    // Fetch category-specific content data (includes all areas: Above, Product, Below)
    // Use effectiveCategoryId to support both SEO URLs and standard category URLs
    // Pass null when effectiveCategoryId is undefined to prevent fetching with URL path
    const {data: categoryContentData, isLoading: isCategoryContentLoading} =
        useOptimizelyPage(effectiveCategoryId || null)

    // Add this helper function at the top of your ProductList component
    const getGridInterruptorPositions = (categoryContentData, breakpoint) => {
        if (!categoryContentData?.items?.[0]?.ProductResultArea) return {}

        const positionMap = {}

        categoryContentData.items[0].ProductResultArea.forEach((item) => {
            if (item.ContentLink?.Expanded?.__typename === 'GridInteruptorBlock') {
                const block = item.ContentLink.Expanded
                let position

                switch (breakpoint) {
                    case 'mobile':
                        position = block.PositionMobile
                        break
                    case 'tablet':
                        position = block.PositionLayout2
                        break
                    case 'desktop':
                        position = block.PositionLayout3
                        break
                    case 'xl':
                        position = block.PositionLayout4
                        break
                    default:
                        position = block.PositionMobile
                }

                if (position) {
                    positionMap[position] = block
                }
            }
        })

        return positionMap
    }

    const currentBreakpoint = useBreakpointValue({
        base: 'mobile',
        md: 'tablet',
        lg: 'desktop',
        xl: 'xl'
    })

    useEffect(() => {
        if (config.app.IS_NOIBU_DISABLED_PLP === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }

        return () => {
            if (config.app.IS_NOIBU_DISABLED_PLP) {
                enableScript('noibu')
            }
        }
    }, [])

    if (effectiveCategoryId) {
        searchParams._refine.push(`cgid=${effectiveCategoryId}`)
    }

    if (!effectiveCategoryId && config.app.ROOT_CATEGORY_ID_GARAGE) {
        searchParams._refine.push(`cgid=${config.app.ROOT_CATEGORY_ID_GARAGE}`)
    }

    searchParams.limit = Number(config.app.LOAD_INITIAL_PRODUCTS_LENGTH) || 12

    /**************** Mutation Actions ****************/
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: createCustomerProductList} = useShopperCustomersMutation(
        'createCustomerProductList'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    /**************** Query Actions ****************/
    // _refine is an invalid param for useProductSearchSSR, we don't want to pass it to API call
    useEffect(() => {
        const hasQueryParams = location.search.length > 0
        const isFirstPage = !new URLSearchParams(location.search).has('page')

        if (!hasQueryParams || isFirstPage) {
            setPage(1)
            setOffset(0)
            setAllProducts?.([])
            setMinPageLoaded(1)
            setMaxPageLoaded(1)
            setSkeletonPlaceholders([])
            setIsLoadingNewPage(false)

            if (productPageMap?.current) {
                productPageMap.current = {}
            }
        }
    }, [location.pathname, location.search])

    // Track category changes to prevent showing wrong products
    const [lastEffectiveCategoryId, setLastEffectiveCategoryId] = useState(effectiveCategoryId)
    const [isCategoryChanging, setIsCategoryChanging] = useState(false)

    // Track filter/sort changes to force reset when filters change (e.g., after back navigation)
    const [lastRefineKey, setLastRefineKey] = useState(() =>
        JSON.stringify(searchParams.refine) + '|' + searchParams.sort
    )

    useEffect(() => {
        if (effectiveCategoryId && effectiveCategoryId !== lastEffectiveCategoryId) {
            setIsCategoryChanging(true)

            // Get page from URL to respect back button navigation
            const urlParams = new URLSearchParams(location.search)
            const urlPage = parseInt(urlParams.get('page')) || 1
            const urlOffset = parseInt(urlParams.get('offset')) || 0

            // Reset everything when category changes
            setAllProducts([])
            setPage(urlPage)
            setOffset(urlOffset)
            setMinPageLoaded(urlPage)
            setMaxPageLoaded(urlPage)
            setSkeletonPlaceholders([])
            setIsLoadingNewPage(false)

            if (productPageMap?.current) {
                productPageMap.current = {}
            }

            setLastEffectiveCategoryId(effectiveCategoryId)
        }
    }, [effectiveCategoryId, lastEffectiveCategoryId, location.search])

    // Reset allProducts when filters or sort change (fixes stale products after back navigation)
    useEffect(() => {
        const currentRefineKey = JSON.stringify(searchParams.refine) + '|' + searchParams.sort
        if (currentRefineKey !== lastRefineKey) {
            setAllProducts([])
            setPage(1)
            setOffset(0)
            setMinPageLoaded(1)
            setMaxPageLoaded(1)
            setSkeletonPlaceholders([])
            setIsLoadingNewPage(false)
            if (productPageMap?.current) {
                productPageMap.current = {}
            }
            setLastRefineKey(currentRefineKey)
        }
    }, [searchParams.refine, searchParams.sort, lastRefineKey])

    const initialOffset = parseInt(urlParams.get('offset')) || 0
    const [offset, setOffset] = useState(initialOffset)
    const [allProducts, setAllProducts] = useState([])

    searchParams.offset = offset

    // Ensure query param is in sync with searchQuery variable
    if (searchQuery && searchParams.q !== searchQuery) {
        searchParams.q = searchQuery
    }

    const {_refine, ...restOfParams} = searchParams
    const limit = searchParams.limit

    // Determine if product search should be enabled
    // Search runs when we have either a categoryId or a search query
    const isProductSearchEnabled = !!(effectiveCategoryId || searchQuery)

    const {
        isLoading,
        isFetched,
        isRefetching,
        data: productSearchResult
    } = useProductSearchSSR(
        {
            parameters: {
                ...restOfParams,
                locale: localeId,
                currency,
                // Ensure categoryId is included so React Query treats different categories as separate queries
                ...(effectiveCategoryId && !searchQuery ? {categoryId: effectiveCategoryId} : {}),
                perPricebook: true,
                expand: [
                    'prices',
                    'images',
                    'custom_properties',
                    'page_meta_tags'
                ],
                refine: _refine
            }
        },
        {
            // Don't keep previous data when category is changing to avoid showing wrong products
            keepPreviousData: !isCategoryChanging,
            enabled: isProductSearchEnabled,
            // Prevent refetch on SSR hydration - data is already fresh from server
            // 60s matches SFCC's cache duration for product data
            staleTime: 60 * 1000
        }
    )

    // === Phase 2: Deferred enrichment (client-side only) ===
    // Same productSearch query but with full expand (adds 'variations', allImages, allVariationProperties).
    // Phase 1 omits these for faster SSR; Phase 2 fetches them client-side.
    const isClient = typeof window !== 'undefined'
    const [accumulatedEnrichment, setAccumulatedEnrichment] = useState(new Map())

    const {data: enrichmentSearchResult} = useProductSearch(
        {
            parameters: {
                ...restOfParams,
                locale: localeId,
                currency,
                ...(effectiveCategoryId && !searchQuery ? {categoryId: effectiveCategoryId} : {}),
                perPricebook: true,
                allImages: true,
                allVariationProperties: true,
                expand: [
                    'prices',
                    'images',
                    'variations',
                    'custom_properties',
                    'page_meta_tags',
                    'promotions'
                ],
                refine: _refine
            }
        },
        {
            enabled: isClient && isProductSearchEnabled && !isSmartSearchParam,
            staleTime: 15 * 60 * 1000
        }
    )

    // Accumulate enrichment across paginated pages
    useEffect(() => {
        const hits = enrichmentSearchResult?.hits || []
        if (hits.length > 0) {
            setAccumulatedEnrichment((prev) => {
                const next = new Map(prev)
                hits.forEach((hit) => next.set(hit.productId, hit))
                return next
            })
        }
    }, [enrichmentSearchResult])

    // Clear enrichment when category changes
    useEffect(() => {
        if (isCategoryChanging) {
            setAccumulatedEnrichment(new Map())
        }
    }, [isCategoryChanging])

    // Clear enrichment when filters change to prevent stale enrichment data
    useEffect(() => {
        setAccumulatedEnrichment(new Map())
    }, [lastRefineKey])

    // Derive enrichment map synchronously from accumulated + current query data.
    // useMemo ensures enrichment is available on the first render after back-navigation
    // (React Query returns cached data synchronously, but useEffect fires after paint).
    const enrichmentMap = useMemo(() => {
        const map = new Map(accumulatedEnrichment)
        const hits = enrichmentSearchResult?.hits || []
        hits.forEach((hit) => map.set(hit.productId, hit))
        return map
    }, [accumulatedEnrichment, enrichmentSearchResult])

    // Merge enrichment into a product hit
    const enrichProduct = useCallback(
        (product) => {
            const enrichment = enrichmentMap.get(product.productId)
            if (!enrichment) return product

            // Phase 2 has allVariationProperties + allImages + variations expand,
            // so the server-side hook computes c_tilesSwatches with correct count + swatchURL.
            // Just spread enrichment over product — Phase 2's c_tilesSwatches replaces Phase 1's.
            return {
                ...product,
                ...enrichment
            }
        },
        [enrichmentMap]
    )

    // GA4 Viewport Tracking
    const currentProducts = isHydrated() ? allProducts : productSearchResult?.hits || []

    // Reset category changing flag when new products arrive
    useEffect(() => {
        if (productSearchResult?.hits?.length > 0 && isCategoryChanging) {
            setIsCategoryChanging(false)
        }
    }, [productSearchResult, isCategoryChanging])

    const createSkeletonPlaceholders = (count, position = 'append') => {
        const skeletons = Array.from({length: count}, (_, index) => ({
            id: `skeleton-${position}-${crypto.randomUUID()}-${index}`,
            type: 'skeleton',
            position
        }))

        setSkeletonPlaceholders((prev) => {
            if (position === 'prepend') {
                return [...skeletons, ...prev]
            } else {
                return [...prev, ...skeletons]
            }
        })
    }

    const removeSkeletonPlaceholders = (position) => {
        setSkeletonPlaceholders((prev) => prev.filter((skeleton) => skeleton.position !== position))
    }

    const initialLabel =
        productSearchResult?.sortingOptions?.find(
            (option) => option.id === productSearchResult?.selectedSortingOption
        )?.label || ''

    const pageTitle =
        productSearchResult?.pageMetaTags?.find((option) => option.id === 'title')?.value || ''

    const isSmartSearch = productSearchResult?.isSmartSearch

    let hasDescriptionMetaTag = false

    let hasKeywordsTag = false

    const [selectedLabel, setSelectedLabel] = useState(initialLabel)

    useEffect(() => {
        if (productSearchResult?.hits?.length) {
            setAllProducts((prev) => {
                const ids = new Set(prev.map((p) => p.productId))
                const newProducts = productSearchResult.hits.filter((p) => !ids.has(p.productId))

                if (newProducts.length === 0) return prev

                if (page < minPageLoaded) {
                    removeSkeletonPlaceholders('prepend')
                } else if (page > maxPageLoaded) {
                    removeSkeletonPlaceholders('append')
                }

                setIsLoadingNewPage(false)

                if (prev.length === 0) return newProducts
                if (page < minPageLoaded) return [...newProducts, ...prev]
                if (page > maxPageLoaded) return [...prev, ...newProducts]

                return prev
            })

            productSearchResult.hits.forEach((p) => {
                productPageMap.current[p.productId] = page
            })

            setMinPageLoaded((prev) => Math.min(prev, page))
            setMaxPageLoaded((prev) => Math.max(prev, page))

            const newLabel =
                productSearchResult?.sortingOptions?.find(
                    (option) => option.id === productSearchResult?.selectedSortingOption
                )?.label || ''

            setSelectedLabel(newLabel)
        }
    }, [productSearchResult])

    useEffect(() => {
        let productContext = null

        try {
            productContext = JSON.parse(sessionStorage.getItem('plp-productContext'))
        } catch {
            productContext = null
        }
        if (!productContext?.productId) return

        if (productContext.page && productContext.page !== page) {
            goToPage(productContext.page)
            return
        }

        const ref = productRefs.current[productContext.productId]
        const hasProducts = allProducts.length > 0 || productSearchResult?.hits?.length > 0

        if (!isLoading && isFetched && hasProducts && ref) {
            // Debounce: cancel pending scroll when deps change (e.g. enrichment arrives),
            // then re-schedule so the final scroll fires after layout has settled.
            const timeoutId = setTimeout(() => {
                productRefs.current[productContext.productId]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
                sessionStorage.removeItem('plp-productContext')
            }, 150)
            return () => clearTimeout(timeoutId)
        }
    }, [page, isLoading, isFetched, allProducts, productSearchResult, enrichmentMap])

    const {error, data: category} = useCategory(
        {
            parameters: {
                id: effectiveCategoryId
            }
        },
        {
            enabled: !isSearch && !!effectiveCategoryId
        }
    )

    // Stable callback to prevent useEffect re-runs
    const stableOnViewItemList = useCallback(
        (products) => {
            if (isSearch) {
                sendViewSearchResultsEvent(searchQuery, products)

                // Braze Modification - Send Braze View Search Results Event
                sendBrazeViewSearchResultsEvent(searchQuery)
            } else {
                sendViewItemListEvent(category, products, false)

                // Braze Modification - Send Braze View Item List Event
                sendBrazeViewItemListEvent(category)
            }
        },
        [isSearch, searchQuery, category]
    )

    const {observeProduct} = useViewportTracking({
        products: currentProducts,
        categoryId: effectiveCategoryId,
        searchQuery,
        visibilityThreshold: 0.5,
        onViewItemList: stableOnViewItemList
    })

    // Apply disallow list to refinements.
    if (productSearchResult?.refinements) {
        productSearchResult.refinements = productSearchResult.refinements.filter(
            ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
        )
    }

    /**************** Response Handling ****************/
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    // Reset scroll position when `isRefetching` becomes `true`.
    useEffect(() => {
        setFiltersLoading(isRefetching)
    }, [isRefetching])

    useEffect(() => {
        setCategory(category)
        // Clear category when navigating away from PLP
        return () => setCategory(null)
    }, [category])

    const basePath = `${location.pathname}${location.search}`

    /**************** Render Variables ****************/
    const showNoResults = !isLoading && productSearchResult && !productSearchResult?.hits
    const {total, sortingOptions} = productSearchResult || {}
    const selectedSortingOptionLabel =
        sortingOptions?.find(
            (option) => option.id === productSearchResult?.selectedSortingOption
        ) ?? sortingOptions?.[0]

    // Get urls to be used for pagination, page size changes, and sorting.
    const sortUrls = useSortUrls({options: sortingOptions})
    const limitUrls = useLimitUrls()

    /**************** Handle Smart Search Loader ****************/
    useEffect(() => {
        if (isFetched && (productSearchResult?.hits?.length > 0 || showNoResults)) {
            window.dispatchEvent(new CustomEvent('plp:ready'))
        }
    }, [isFetched, productSearchResult, showNoResults])

    /**************** Action Handlers ****************/
    // Toast styles - empty object to avoid overriding default fixed positioning
    const wishlistToastStyles = {}
    const {data: wishlist, refetch: refetchWishlist} = useWishList()
    
    const addItemToWishlist = async (product, index) => {
        setWishlistLoading([...wishlistLoading, product.productId])

        const listId = await getWishlistId({
            wishlist,
            customerId,
            refetchWishlist,
            createCustomerProductList
        })
        if (!listId) {
            setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
            return
        }
        const isMaximumNumberOfItemsReached = wishlist?.customerProductListItems?.length >= 100

        if (isMaximumNumberOfItemsReached) {
            showCustomToast({
                message: formatMessage(
                    {
                        id: 'cart.info.full_message',
                        defaultMessage:
                            'You have reached the maximum number of items for your Wishlist. <a>See Wishlist</a>'
                    },
                    {
                        a: (chunks) => (
                            <Link
                                href="/account/wishlist"
                                display="block"
                                mt={2}
                                textDecoration="underline"
                            >
                                {chunks}
                            </Link>
                        )
                    }
                ),
                styles: wishlistToastStyles
            })
        } else {
            await createCustomerProductListItem(
                {
                    parameters: {customerId, listId},
                    body: {
                        quantity: 1,
                        public: true,
                        priority: 1,
                        type: 'product',
                        productId: product.productId
                    }
                },
                {
                    onError: () => {
                        showCustomToast({
                            message: formatMessage(API_ERROR_MESSAGE),
                            styles: wishlistToastStyles
                        })
                    },
                    onSuccess: () => {
                        showCustomToast({
                            message: formatMessage({
                                id: 'cart.info.item_added',
                                defaultMessage: 'Added to Wishlist'
                            }),
                            action: (
                                <Button
                                    variant="link"
                                    fontWeight="medium"
                                    fontSize="sm"
                                    onClick={() => navigate('/account/wishlist')}
                                    colorScheme="white"
                                    textDecoration="underline"
                                >
                                    {formatMessage({
                                        id: 'cart.info.view_list',
                                        defaultMessage: 'View List'
                                    })}
                                </Button>
                            ),
                            styles: wishlistToastStyles
                        })
                        sendWishlistEvent(product, false, index)

                        // Braze Modification - Send Braze Wishlist Event
                        sendBrazeWishlistEvent(product, false, effectiveCategoryId)
                    },
                    onSettled: () => {
                        setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
                    }
                }
            )
        }
    }

    const removeItemFromWishlist = async (product, index) => {
        setWishlistLoading([...wishlistLoading, product.productId])

        if (!wishlist?.id) {
            setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
            return
        }
        const listId = wishlist.id
        const itemId = wishlist.customerProductListItems.find(
            (i) => i.productId === product.productId
        ).id

        await deleteCustomerProductListItem(
            {
                body: {},
                parameters: {customerId, listId, itemId}
            },
            {
                onError: () => {
                    showCustomToast({
                        message: formatMessage(API_ERROR_MESSAGE),
                        styles: wishlistToastStyles
                    })
                },
                onSuccess: () => {
                    showCustomToast({
                        message: formatMessage({
                            id: 'cart.info.item_removed',
                            defaultMessage: 'Item removed'
                        }),
                        styles: wishlistToastStyles
                    })
                    sendWishlistEvent(product, true, index)

                    // Braze Modification - Send Braze Wishlist Event
                    sendBrazeWishlistEvent(product, true, effectiveCategoryId)
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
                }
            }
        )
    }

    // Toggles filter on and off
    const toggleFilter = (value, attributeId, selected, allowMultiple = true) => {
        const searchParamsCopy = {...searchParams}

        // Remove pagination params so the reset effect properly clears allProducts.
        delete searchParamsCopy.offset
        delete searchParamsCopy.page

        // If we aren't allowing for multiple selections, simply clear any value set for the
        // attribute, and apply a new one if required.
        if (!allowMultiple) {
            const previousValue = searchParamsCopy.refine[attributeId]
            delete searchParamsCopy.refine[attributeId]

            // Note the loose comparison, for "string != number" checks.
            if (!selected && value.value != previousValue) {
                searchParamsCopy.refine[attributeId] = value.value
            }
        } else {
            // Get the attibute value as an array.
            let attributeValue = searchParamsCopy.refine[attributeId] || []

            // Ensure that the value is still converted into an array if it's a `string` or `number`.
            if (typeof attributeValue === 'string') {
                attributeValue = attributeValue.split('|')
            } else if (typeof attributeValue === 'number') {
                attributeValue = [attributeValue]
            }

            // Either set the value, or filter the value out.
            if (!selected) {
                attributeValue.push(value.value)
            } else {
                // Note the loose comparison, for "string != number" checks.
                attributeValue = attributeValue?.filter((v) => v != value.value)
            }

            // Update the attribute value in the new search params.
            searchParamsCopy.refine[attributeId] = attributeValue

            // If the update value is an empty array, remove the current attribute key.
            if (searchParamsCopy.refine[attributeId].length === 0) {
                delete searchParamsCopy.refine[attributeId]
            }
        }

        if (isSearch) {
            navigate(`/search?${stringifySearchParams(searchParamsCopy)}`)
        } else {
            history.replace(
                `${categoryUrlBuilder(category)}?${stringifySearchParams(searchParamsCopy)}`
            )
        }
    }

    // Clears all filters
    const resetFilters = () => {
        const newSearchParams = {
            ...searchParams,
            refine: []
        }
        delete newSearchParams.page
        delete newSearchParams.offset
        if (isSearch) {
            navigate(`/search?${stringifySearchParams(newSearchParams)}`)
        } else {
            history.replace(
                `${categoryUrlBuilder(category)}?${stringifySearchParams(newSearchParams)}`
            )
        }
    }

    const goToPage = (newPage) => {
        const newOffset = (newPage - 1) * limit
        setPage(newPage)
        setOffset(newOffset)
        setIsLoadingNewPage(true)

        if (newPage < minPageLoaded) {
            createSkeletonPlaceholders(limit, 'prepend')
        } else if (newPage > maxPageLoaded) {
            createSkeletonPlaceholders(limit, 'append')
        }

        const newParams = new URLSearchParams(location.search)
        newParams.set('page', newPage)

        if (newPage > 1) {
            newParams.set('offset', newOffset)
        } else {
            newParams.delete('offset')
        }

        const categoryUrl = category ? categoryUrlBuilder(category) : location.pathname
        history.replace(`${categoryUrl}?${newParams.toString()}`)
    }

    const handleClick = () => {
        if (allProducts.length < total) {
            setOffset((prev) => {
                return prev + limit
            })
        }
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (productSearchResult) {
            if (isSearch) {
                try {
                    einstein.sendViewSearch(searchQuery, productSearchResult)
                    // GTM events now handled by viewport tracking
                } catch (err) {
                    logger.error('Einstein sendViewSearch error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, searchQuery}
                    })
                }
                activeData.sendViewSearch(searchParams, productSearchResult)
            } else {
                try {
                    if (category) {
                        einstein.sendViewCategory(category, productSearchResult)
                    }
                    // GTM events now handled by viewport tracking
                } catch (err) {
                    logger.error('Einstein sendViewCategory error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, category}
                    })
                }
                activeData.sendViewCategory(searchParams, category, productSearchResult)
            }
        }
    }, [productSearchResult])

    useEffect(() => {
        if (location.pathname !== currentPath) {
            setIsInitialLoad(true)
            setCurrentPath(location.pathname)
        }
    }, [location.pathname, currentPath])

    useEffect(() => {
        if (!isLoading && productSearchResult?.hits) {
            setIsInitialLoad(false)
        }
    }, [isLoading, productSearchResult])

    /*-------- Seo Footer: START --------*/
    const seoFooterStyleBlockContainerStyle = {
        maxWidth: {lg: '692px'},
        margin: {lg: '0 auto'},
        paddingTop: {base: 16, lg: 12}
    }

    const getSeoFooterStyleBlock = (categoryContentData) => {
        if (!categoryContentData?.items?.[0]?.BelowResultArea) {
            return null
        }

        const belowResultArea = categoryContentData.items[0].BelowResultArea

        // Find the item with SeoFooterStyleBlock typename
        const seoFooterItem = belowResultArea.find(
            (item) => item.ContentLink?.Expanded?.__typename === 'SeoFooterStyleBlock'
        )

        return seoFooterItem?.ContentLink?.Expanded || null
    }

    const seoFooterData = useMemo(() => {
        const result = getSeoFooterStyleBlock(categoryContentData)
        return (
            result || {
                loadFromBM: true,
                __typename: 'SeoFooterStyleBlock'
            }
        )
    }, [categoryContentData])
    /*-------- Seo Footer: END --------*/

    /**************** Error Handling ****************/
    const shouldRenderSeoNotFound =
        hasSEOUrlParams && !effectiveCategoryId && (serverSeoNotFound || clientSeoNotFound)

    if (shouldRenderSeoNotFound) {
        return <PageNotFound />
    }

    const errorStatus = error?.response?.status
    switch (errorStatus) {
        case undefined:
            // No Error.
            break
        case 404:
            return <PageNotFound />
        default:
            throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
    }

    if (category && category.c_categoryIsOnline === false) {
        return <PageNotFound />
    }

    const hasSelectedFilters = productSearchResult?.selectedRefinements !== undefined && Object.keys(productSearchResult.selectedRefinements)?.length > 0

    // Render selected filters for desktop
    const renderDesktopSelectedFilters = () => (
        <HideOnMobile>
            <Box>
                <SelectedRefinements
                    filters={productSearchResult?.refinements?.filter((refinement) => {
                        return refinement?.values?.some((value) => value.hitCount > 0)
                    })}
                    toggleFilter={toggleFilter}
                    handleReset={resetFilters}
                    selectedFilterValues={productSearchResult?.selectedRefinements}
                />
            </Box>
        </HideOnMobile>
    )

    // Render selected filters for mobile
    const renderMobileSelectedFilters = () => (
        <HideOnDesktop>
            <Box>
                <SelectedRefinements
                    filters={productSearchResult?.refinements}
                    toggleFilter={toggleFilter}
                    handleReset={resetFilters}
                    selectedFilterValues={productSearchResult?.selectedRefinements}
                />
            </Box>
        </HideOnDesktop>
    )

    return (
        <Box
            className="sf-product-list-page"
            data-testid="sf-product-list-page"
            layerStyle="PLPpage"
            {...rest}
        >
            <Seo
                title={pageTitle || (category?.pageTitle ?? searchQuery)}
                noIndex={isSearch}
                canonicalUrl={category?.c_canonicalURL}
            >
                {productSearchResult?.pageMetaTags?.map((tag) => {
                    if (tag.id != 'title') {
                        if (tag.id == 'description') {
                            hasDescriptionMetaTag = true
                        }

                        if (tag.id == 'keywords') {
                            hasKeywordsTag = true
                        }

                        return (
                            <meta
                                key={tag.id}
                                {...(tag.id.startsWith('og:')
                                    ? {property: tag.id, content: tag.value}
                                    : {name: tag.id, content: tag.value})}
                            />
                        )
                    }
                })}

                {!hasDescriptionMetaTag && (
                    <meta name="description" content={category?.pageDescription ?? searchQuery} />
                )}
                {!hasKeywordsTag && <meta name="keywords" content={category?.pageKeywords} />}
                {category?.c_canonicalURL && (
                    <link rel="canonical" href={category.c_canonicalURL} />
                )}
                {generateAlternateUrls(category?.c_alternateURLs)}
            </Seo>
            {!showNoResults && (
                <>
                    <AbovePageHeader />

                    {/* Header */}
                    <Box marginBottom={{base: 6, lg: 8}}>
                        <PageHeader
                            searchQuery={searchQuery}
                            category={category}
                            productSearchResult={productSearchResult}
                            categoryContentData={categoryContentData}
                            isCategoryContentLoading={isCategoryContentLoading}
                        />
                    </Box>
                </>
            )}

            {showNoResults && <EmptySearchResultsTitle hasSelectedFilters={hasSelectedFilters} />}

            <PageFilters
                category={category}
                productSearchResult={productSearchResult}
                isLoading={isLoading}
                toggleFilter={toggleFilter}
                searchParams={searchParams}
                resetFilters={resetFilters}
                onSortClick={() => setSortOpen(true)}
                onFiltersClick={() => onOpen()}
                isVisible={!showNoResults || hasSelectedFilters}
            />

            {showNoResults && hasSelectedFilters && (
                <Box mb={16}>
                    {renderDesktopSelectedFilters()}
                    {renderMobileSelectedFilters()}
                </Box>
            )}

            {showNoResults ? (
                <EmptySearchResults />
            ) : (
                <>
                    {!isLoading &&
                        !isRefetching &&
                        (minPageLoaded > 1 || (allProducts.length === 0 && page > 1)) && (
                            <Flex justifyContent="center" gap={4} my={8}>
                                <Button
                                    variant="solid"
                                    onClick={() => goToPage(minPageLoaded - 1)}
                                    width="100%"
                                    maxWidth={{base: '100%', lg: '300px'}}
                                    bg="black"
                                    color="white"
                                    fontSize="md"
                                    textTransform="uppercase"
                                >
                                    {formatMessage({
                                        id: 'plp.button.show.previous',
                                        defaultMessage: 'Show Previous'
                                    })}
                                </Button>
                            </Flex>
                        )}

                    {renderDesktopSelectedFilters()}
                    {renderMobileSelectedFilters()}

                    <Flex
                        display={{base: 'flex', md: 'none'}}
                        justify="space-between"
                        align="center"
                        mb={2}
                    >
                        <Text fontSize="sm" color="gray.600">
                            <FormattedMessage
                                id="product_list.item_count"
                                defaultMessage="{count} {count, plural, one {Item} other {Items}}"
                                values={{count: productSearchResult?.total || 0}}
                            />
                        </Text>
                        {!isSmartSearch && (
                            <Button
                                rightIcon={<ChevronDownIcon boxSize={5} />}
                                variant="sortSelect"
                                onClick={() => setSortOpen(true)}
                            >
                                <FormattedMessage
                                    id="product_list.drawer.title.sort_by"
                                    defaultMessage="Sort By"
                                />
                            </Button>
                        )}
                    </Flex>

                    {/* Body  */}
                    <Grid templateColumns={{base: '1fr', md: '1fr'}} columnGap={6}>
                        <Box>
                            <SimpleGrid
                                columns={[2, 2, 4, 4]}
                                spacingX={2}
                                spacingY={{base: 6, lg: 8}}
                            >
                                {isHydrated() &&
                                ((isRefetching && !allProducts?.length) ||
                                    !productSearchResult ||
                                    isCategoryChanging) ? (
                                    new Array(searchParams.limit)
                                        .fill(0)
                                        .map((value, index) => <ProductTileSkeleton key={index} />)
                                ) : (
                                    <>
                                        {skeletonPlaceholders
                                            ?.filter((skeleton) => skeleton.position === 'prepend')
                                            .map((skeleton) => (
                                                <ProductTileSkeleton key={skeleton.id} />
                                            ))}

                                        {(() => {
                                            // Always use productSearchResult for SSR and initial client render
                                            // Only switch to allProducts when pagination is actively used
                                            const products =
                                                allProducts.length > 0
                                                    ? allProducts
                                                    : productSearchResult?.hits
                                            const gridInterruptorPositions =
                                                getGridInterruptorPositions(
                                                    categoryContentData,
                                                    currentBreakpoint
                                                )
                                            const itemsToRender = []

                                            products?.forEach((productSearchItem, index) => {
                                                const position = index + 1 // Grid positions are 1-based
                                                const productId = productSearchItem.productId
                                                const isInWishlist =
                                                    !!wishlist?.customerProductListItems?.find(
                                                        (item) => item.productId === productId
                                                    )

                                                // Add grid interruptor at this position
                                                if (gridInterruptorPositions[position]) {
                                                    itemsToRender.push(
                                                        <GridInteruptorBlock
                                                            key={`grid-interruptor-${position}`}
                                                            data={
                                                                gridInterruptorPositions[position]
                                                            }
                                                            position={position}
                                                            breakpoint={currentBreakpoint}
                                                        />
                                                    )
                                                }

                                                // Add the product tile with viewport tracking
                                                itemsToRender.push(
                                                    <ProductTile
                                                        key={productSearchItem.productId}
                                                        data-testid={`sf-product-tile-${productSearchItem.productId}`}
                                                        data-product-id={
                                                            productSearchItem.productId
                                                        }
                                                        ref={mergeRefs((el) => {
                                                            if (el)
                                                                productRefs.current[productId] = el
                                                        }, observeProduct)}
                                                        product={enrichProduct(productSearchItem)}
                                                        enableFavourite={true}
                                                        isFavourite={isInWishlist}
                                                        isRefreshingData={isRefetching && isFetched}
                                                        imageViewType={PRODUCT_LIST_IMAGE_VIEW_TYPE}
                                                        selectableAttributeId={
                                                            PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID
                                                        }
                                                        showSwatches={true}
                                                        showVideo={true}
                                                        onClick={() => {
                                                            if (searchQuery) {
                                                                einstein.sendClickSearch(
                                                                    searchQuery,
                                                                    productSearchItem
                                                                )
                                                                // GTM Modification
                                                                sendSelectItemEvent(
                                                                    productSearchItem,
                                                                    {
                                                                        id: 'search_results_page',
                                                                        name: 'search results'
                                                                    },
                                                                    index
                                                                )
                                                            } else if (category) {
                                                                setTempRecommendation(category)

                                                                einstein.sendClickCategory(
                                                                    category,
                                                                    productSearchItem
                                                                )
                                                                // GTM Modification
                                                                sendSelectItemEvent(
                                                                    productSearchItem,
                                                                    category,
                                                                    index
                                                                )
                                                            }

                                                            const actualPage =
                                                                productPageMap.current?.[
                                                                    productId
                                                                ] || page
                                                            const actualOffset =
                                                                (actualPage - 1) * limit

                                                            sessionStorage.setItem(
                                                                'plp-productContext',
                                                                JSON.stringify({
                                                                    productId,
                                                                    page: actualPage
                                                                })
                                                            )

                                                            const params = new URLSearchParams(
                                                                location.search
                                                            )
                                                            params.set('page', actualPage)

                                                            if (actualPage > 1) {
                                                                params.set('offset', actualOffset)
                                                            } else {
                                                                params.delete('offset')
                                                            }

                                                            history.replace(
                                                                `${
                                                                    location.pathname
                                                                }?${params.toString()}`
                                                            )
                                                        }}
                                                        onFavouriteToggle={(toBeFavourite) => {
                                                            const action = toBeFavourite
                                                                ? addItemToWishlist
                                                                : removeItemFromWishlist
                                                            return action(productSearchItem, index)
                                                        }}
                                                        dynamicImageProps={{
                                                            widths: [
                                                                '50vw',
                                                                '50vw',
                                                                '25vw',
                                                                '25vw',
                                                                '25vw'
                                                            ],
                                                            imageProps: {
                                                                loading:
                                                                    index < 4 ? 'eager' : 'lazy',
                                                                fetchpriority:
                                                                    index === 0 ? 'high' : 'auto',
                                                                decoding:
                                                                    index < 4 ? 'sync' : 'async'
                                                            }
                                                        }}
                                                        videoProps={{
                                                            preload: index < 4 ? 'auto' : 'none'
                                                        }}
                                                    />
                                                )
                                            })

                                            return itemsToRender
                                        })()}

                                        {skeletonPlaceholders
                                            ?.filter((skeleton) => skeleton.position === 'append')
                                            .map((skeleton) => (
                                                <ProductTileSkeleton key={skeleton.id} />
                                            ))}
                                    </>
                                )}
                            </SimpleGrid>

                            {(() => {
                                // For SSR and initial render, use productSearchResult?.hits
                                // For client-side pagination, use allProducts
                                const currentProducts =
                                    allProducts.length > 0
                                        ? allProducts
                                        : productSearchResult?.hits || []
                                const hasMore = currentProducts.length < total

                                return (
                                    hasMore && (
                                        <Flex justifyContent="center" gap={4} mt={10}>
                                            <Button
                                                variant="solid"
                                                onClick={() => goToPage(maxPageLoaded + 1)}
                                                width="100%"
                                                maxWidth={{base: '100%', lg: '300px'}}
                                                bg="black"
                                                color="white"
                                                fontSize="md"
                                                textTransform="uppercase"
                                            >
                                                {formatMessage({
                                                    id: 'plp.button.show.more',
                                                    defaultMessage: 'Show More'
                                                })}
                                            </Button>
                                        </Flex>
                                    )
                                )
                            })()}

                            {/* Footer */}
                            <Flex
                                display="none"
                                justifyContent={['center', 'center', 'flex-start']}
                                paddingTop={8}
                            >
                                {/*
                                Our design doesn't call for a page size select. Show this element if you want
                                to add one to your design.
                            */}
                                <Select
                                    value={basePath}
                                    onChange={({target}) => {
                                        history.push(target.value)
                                    }}
                                >
                                    {limitUrls.map((href, index) => (
                                        <option key={href} value={href}>
                                            {DEFAULT_LIMIT_VALUES[index]}
                                        </option>
                                    ))}
                                </Select>
                            </Flex>
                        </Box>
                    </Grid>

                    {/*
                        Seo Footer data loaded from BM but without styling.
                        If there will be a configuration on CMS with "SeoFooterStyleBlock" typename
                        under the categoryContentData?.items?.[0]?.BelowResultArea this will render with
                        OptimizelyArea component and will apply the styling from CMS for the specific category
                        When the text will be moved to CMS it should remain here only the content rendered
                        with OptimizelyArea component.
                    */}
                    {seoFooterData &&
                        seoFooterData?.loadFromBM &&
                        renderLayoutsSwitch(seoFooterData, {
                            categoryName: category?.name,
                            seoContent: category?.c_categorySEOSlot,
                            seoFooterStyleBlockContainerStyle: seoFooterStyleBlockContainerStyle
                        })}

                    {/* Below Result Area - CMS Content */}
                    {categoryContentData?.items?.[0]?.BelowResultArea && (
                        <OptimizelyArea
                            content={categoryContentData.items[0]}
                            area="BelowResultArea"
                            marginTop={{base: 8, lg: 12}}
                            seoContent={category?.c_categorySEOSlot}
                            categoryName={category?.name}
                            seoFooterStyleBlockContainerStyle={seoFooterStyleBlockContainerStyle}
                        />
                    )}
                </>
            )}

            <Drawer
                placement="bottom"
                isOpen={sortOpen}
                onClose={() => setSortOpen(false)}
                size="sm"
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={false}
                height="50%"
            >
                <DrawerOverlay />
                <DrawerContent marginTop={0}>
                    <DrawerHeader padding="24px 30px 0 32px" boxShadow="none">
                        <Text color="grey.800" fontSize="14px" fontWeight={400}>
                            <FormattedMessage
                                defaultMessage="Sort By"
                                id="product_list.drawer.title.sort_by"
                            />
                        </Text>
                    </DrawerHeader>
                    <DrawerCloseButton top="24px" right="30px" w="20px" h="20px" />
                    <DrawerBody minH={'386px'} px={0}>
                        {productSearchResult?.sortingOptions?.map((option, idx) => {
                            const label = option?.label
                            const id = option?.id
                            const href = sortUrls[idx]

                            return (
                                <Button
                                    width="full"
                                    onClick={() => {
                                        setSortOpen(false)
                                        history.push(href)
                                    }}
                                    justifyContent="start"
                                    gap="16px"
                                    key={idx}
                                    py="16px"
                                    px="32px"
                                    fontSize="16px"
                                    variant="sortLink"
                                >
                                    <Text alignItems="center" display="flex" gap="16px">
                                        {id === productSearchResult?.selectedSortingOption && (
                                            <Box display="inline-block" w="20px" h="20px">
                                                <CheckIcon />
                                            </Box>
                                        )}{' '}
                                        {label}
                                    </Text>
                                </Button>
                            )
                        })}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.propTypes = {
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object
}

// PWA Kit getProps method to pass server-side data to the component

export default ProductList

